import { type CSSProperties, type ReactElement } from 'react';
import {
  LIFECYCLE_VISUAL,
  UNKNOWN_LIFECYCLE_VISUAL,
  parseLifecycleOperation,
  type LifecycleOperation,
} from './lifecycle';

/**
 * Wave E.3 — Lifecycle operation badge.
 *
 * Small pill rendered next to a document title in the doc list, the shared
 * selected-file header and the expanded-navigation header. Color and label
 * come from `LIFECYCLE_VISUAL` so every call site stays visually consistent.
 *
 * The value is parsed through the canonical non-defaulting
 * `parseLifecycleOperation`: a known (case-insensitive) operation renders its
 * pill, and an absent or unrecognised value renders an honest "Unknown" —
 * never a fabricated "NEW". That is what keeps the expanded-navigation badge
 * honest without forking the rule.
 *
 * For DELETE, the badge color is red and consumers should also strike
 * through the document title — the `strikethrough` flag on the visual
 * tells them whether to do that.
 */

export interface DocumentBadgeProps {
  readonly operation: LifecycleOperation | string | null | undefined;
  /** Optional override for visual size; defaults to 'sm'. */
  readonly size?: 'sm' | 'md';
}

const baseStyle: CSSProperties = {
  display: 'inline-block',
  borderRadius: 999,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  fontWeight: 700,
  whiteSpace: 'nowrap',
  userSelect: 'none',
};

const sizeStyle: Record<NonNullable<DocumentBadgeProps['size']>, CSSProperties> = {
  sm: { fontSize: 9, padding: '1px 6px', lineHeight: 1.4 },
  md: { fontSize: 11, padding: '3px 10px', lineHeight: 1.4 },
};

export function DocumentBadge({ operation, size = 'sm' }: DocumentBadgeProps): ReactElement {
  const op = parseLifecycleOperation(operation as string | null | undefined);
  const visual = op ? LIFECYCLE_VISUAL[op] : UNKNOWN_LIFECYCLE_VISUAL;
  return (
    <span
      role="status"
      aria-label={`Lifecycle operation: ${visual.label}`}
      data-testid={`document-badge-${op ?? 'unknown'}`}
      style={{
        ...baseStyle,
        ...sizeStyle[size],
        background: visual.background,
        color: visual.color,
        border: `1px solid ${visual.border}`,
      }}
    >
      {visual.label}
    </span>
  );
}
